import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import NotificationsIcon from "@mui/icons-material/NotificationsNone";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonRemoveIcon from "@mui/icons-material/PersonRemoveOutlined";
import BlockIcon from "@mui/icons-material/Block";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import HandshakeIcon from "@mui/icons-material/HandshakeOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import FlagIcon from "@mui/icons-material/OutlinedFlag";
import AddFriendDialog from "./AddFriendDialog";
import ReportDialog from "./ReportDialog";
import { blockConfirmMessage } from "../../utils/moderation";
import PersonAvatar from "../PersonAvatar";
import { useAuthStore } from "../../store";

const api = async (user, path, method = "GET", body) => {
  const idToken = await user.getIdToken();
  const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Operacja nie powiodła się");
  return data;
};

const PersonHeader = ({ person }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
    <PersonAvatar name={person.full_name} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body1" fontWeight={600} noWrap>
        {person.full_name}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap>
        {person.email}
      </Typography>
    </Box>
  </Box>
);

const PersonCard = ({ person, children, testId }) => (
  <Card variant="outlined" data-testid={testId}>
    <CardContent
      sx={{
        py: 1.5,
        px: 2,
        "&:last-child": { pb: 1.5 },
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <PersonHeader person={person} />
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>{children}</Box>
    </CardContent>
  </Card>
);

// "Zgłoś" / "Zablokuj" entries, shared by the friend menu and the invite menu.
// MUI Menu needs its items as direct children, so this returns an array.
const moderationItems = ({ pick, onReport, onBlock }) => [
  <MenuItem key="report" onClick={pick(onReport)}>
    <ListItemIcon>
      <FlagIcon fontSize="small" />
    </ListItemIcon>
    <ListItemText>Zgłoś</ListItemText>
  </MenuItem>,
  <MenuItem key="block" onClick={pick(onBlock)} sx={{ color: "error.main" }}>
    <ListItemIcon>
      <BlockIcon fontSize="small" color="error" />
    </ListItemIcon>
    <ListItemText>Zablokuj</ListItemText>
  </MenuItem>,
];

// "⋮" with only "Zgłoś" / "Zablokuj", for people who aren't friends yet (an incoming invite).
const ModerationMenu = ({ busy, onReport, onBlock }) => {
  const [anchor, setAnchor] = useState(null);
  const pick = (action) => () => {
    setAnchor(null);
    action();
  };
  return (
    <>
      <IconButton size="small" aria-label="Zgłoś lub zablokuj" data-testid="card-menu" disabled={busy} onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {moderationItems({ pick, onReport, onBlock })}
      </Menu>
    </>
  );
};

// A friend: who they are, a "⋮" menu for the rare and destructive actions,
// and a footer saying where we stand on booking together ("Zapisy razem", a
// separate mutual consent) and, once that is on, on passes both ways.
const FriendCard = ({
  person,
  friendship,
  asked,
  granted,
  busy,
  onProposeBooking,
  onWithdrawBooking,
  onDisableBooking,
  onAskForPasses,
  onRevokePasses,
  onRemove,
  onReport,
  onBlock,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const pick = (action) => () => {
    setMenuAnchor(null);
    action();
  };
  return (
    <Card variant="outlined" data-testid="friend" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
          <PersonHeader person={person} />
          <IconButton
            size="small"
            aria-label="Więcej opcji"
            data-testid="card-menu"
            disabled={busy}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {friendship.booking_status === "pending" && friendship.booking_requested_by_me && (
              <MenuItem onClick={pick(onWithdrawBooking)}>
                <ListItemIcon>
                  <BlockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Wycofaj propozycję wspólnych zapisów</ListItemText>
              </MenuItem>
            )}
            {friendship.booking_together && (
              <MenuItem onClick={pick(onDisableBooking)}>
                <ListItemIcon>
                  <BlockIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText>Wyłącz wspólne zapisy</ListItemText>
              </MenuItem>
            )}
            {granted?.status === "accepted" && (
              <MenuItem onClick={pick(onRevokePasses)}>
                <ListItemIcon>
                  <BlockIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText>Cofnij zgodę na karnety</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={pick(onRemove)} sx={{ color: "error.main" }}>
              <ListItemIcon>
                <PersonRemoveIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Usuń ze znajomych</ListItemText>
            </MenuItem>
            {moderationItems({ pick, onReport, onBlock })}
          </Menu>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }} data-testid="booking-together">
          <GroupsIcon fontSize="small" sx={{ color: "text.secondary" }} />
          {friendship.booking_together && (
            <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="Zapisy razem" />
          )}
          {friendship.booking_status === "none" && (
            <Button size="small" variant="text" disabled={busy} onClick={onProposeBooking} sx={{ textTransform: "none", px: 1 }}>
              Zaproponuj wspólne zapisy
            </Button>
          )}
          {friendship.booking_status === "pending" && friendship.booking_requested_by_me && (
            <Chip size="small" variant="outlined" icon={<HourglassEmptyIcon />} label="Propozycja wysłana" />
          )}
          {friendship.booking_status === "pending" && !friendship.booking_requested_by_me && (
            <Chip size="small" color="warning" variant="outlined" icon={<HourglassEmptyIcon />} label="Czeka na Twoją odpowiedź" />
          )}
        </Box>
        {friendship.booking_together && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1 }}>
            <ConfirmationNumberIcon fontSize="small" sx={{ color: "text.secondary" }} />
            {!asked && (
              <Button size="small" variant="text" disabled={busy} onClick={onAskForPasses} sx={{ textTransform: "none", px: 1 }}>
                Poproś o korzystanie z karnetów
              </Button>
            )}
            {asked?.status === "pending" && (
              <Chip size="small" variant="outlined" icon={<HourglassEmptyIcon />} label="Prośba o karnety wysłana" />
            )}
            {asked?.status === "accepted" && (
              <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="Możesz korzystać z karnetów" />
            )}
            {granted?.status === "accepted" && (
              <Chip size="small" color="info" variant="outlined" icon={<HandshakeIcon />} label="Korzysta z Twoich karnetów" />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const formatWhen = (iso) => {
  // The backend stores UTC without a zone suffix.
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  return date.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// What friends did that concerns me: booked me in, signed me out, answered a
// request... Unread ones are highlighted on the visit that marks them read.
// Each can be deleted; older ones are fetched a page at a time.
const NotificationsSection = ({ notifications, hasMore, onLoadMore, onDelete, onClear }) => {
  const [expanded, setExpanded] = useState(false);
  if (!notifications.length) return null;
  const shown = expanded ? notifications : notifications.slice(0, 3);
  const hidden = notifications.length - shown.length;
  return (
    <Box sx={{ mb: 2 }} data-testid="friend-notifications">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <NotificationsIcon fontSize="small" /> Powiadomienia
        </Typography>
        <Button size="small" color="inherit" onClick={onClear} data-testid="clear-notifications">
          Wyczyść
        </Button>
      </Box>
      <Stack spacing={1}>
        {shown.map((n) => (
          <Card
            key={n.id}
            variant="outlined"
            data-testid="friend-notification"
            data-unread={n.read_at ? "false" : "true"}
            sx={n.read_at ? undefined : { borderColor: "primary.main", bgcolor: "action.hover" }}
          >
            <CardContent sx={{ py: 1.25, pl: 2, pr: 1, "&:last-child": { pb: 1.25 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={n.read_at ? 500 : 700} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      {n.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatWhen(n.created_at)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                    {n.message}
                  </Typography>
                </Box>
                <IconButton size="small" aria-label="Usuń powiadomienie" data-testid="delete-notification" onClick={() => onDelete(n.id)} sx={{ mt: -0.5 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
      <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
        {hidden > 0 && (
          <Button size="small" onClick={() => setExpanded(true)}>
            {`Pokaż wszystkie (${notifications.length}${hasMore ? "+" : ""})`}
          </Button>
        )}
        {expanded && hasMore && (
          <Button size="small" onClick={onLoadMore}>
            Pokaż starsze
          </Button>
        )}
        {expanded && notifications.length > 3 && (
          <Button size="small" onClick={() => setExpanded(false)}>
            Pokaż mniej
          </Button>
        )}
      </Box>
    </Box>
  );
};

const Section = ({ title, children }) => (
  <>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Stack spacing={1} sx={{ mb: 2 }}>
      {children}
    </Stack>
  </>
);

// What a pass-use permission means, told from the side of the person reading
// it: the owner who grants it, or the friend who asks for it.
const PassUseExplanation = ({ perspective, name }) => {
  const asOwner = perspective === "owner";
  return (
    <Box component="ul" sx={{ pl: 2.5, my: 1, "& li": { mb: 0.75 } }}>
      <li>
        {asOwner ? (
          <>Gdy <strong>{name}</strong> zapisze Cię na zajęcia razem ze sobą, za <strong>Twoje</strong> miejsce zejdzie 1 wejście z Twojego karnetu — tylko jeśli obejmuje te zajęcia.</>
        ) : (
          <>Gdy zapiszesz <strong>{name}</strong> na zajęcia razem ze sobą, za miejsce <strong>{name}</strong> zejdzie 1 wejście z jego/jej karnetu — tylko jeśli obejmuje te zajęcia.</>
        )}
      </li>
      <li>
        {asOwner
          ? "Twój karnet nigdy nie zapłaci za miejsce tej osoby ani nikogo innego."
          : "Ten karnet nigdy nie zapłaci za Twoje miejsce ani za nikogo innego."}
      </li>
      <li>
        {asOwner
          ? "Ta osoba nie widzi Twoich karnetów — widzi tylko, że Twoje miejsce jest opłacone."
          : "Nie zobaczysz karnetów tej osoby — tylko informację, że jej miejsce jest opłacone."}
      </li>
      <li>Po wypisaniu się wejście wraca na ten sam karnet.</li>
      <li>
        {asOwner ? "Zgodę możesz cofnąć" : "Zgodę można cofnąć"} w każdej chwili w zakładce „Znajomi”. Nie dotyczy zajęć duo/trio.
      </li>
    </Box>
  );
};

// What booking together ("Zapisy razem") lets both of you do.
const BookingExplanation = ({ name }) => (
  <Box component="ul" sx={{ pl: 2.5, my: 1, "& li": { mb: 0.75 } }}>
    <li>
      <strong>{name}</strong> będzie mógł/mogła zapisywać Cię na zajęcia razem ze sobą, a Ty — {name}.
    </li>
    <li>Za miejsce osoby zapisanej przez znajomego płaci ten, kto zapisuje (chyba że pokryje je jej własny karnet).</li>
    <li>Każde z Was może w każdej chwili wyłączyć wspólne zapisy w zakładce „Znajomi”.</li>
  </Box>
);

const HowItWorksDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Jak działają znajomi?</DialogTitle>
    <DialogContent>
      <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
        Znajomi widzą Twoje treningi na tablicy w aplikacji. Wspólne zapisy włączacie osobno, oboje musicie się zgodzić.
      </Typography>
      <Typography variant="subtitle2">Zapisy razem</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Znajomych, z którymi macie włączone wspólne zapisy, możesz zapisać na zajęcia razem ze sobą — zaznaczasz ich w formularzu zapisu.
        Ty zarządzasz rezerwacją i płacisz za miejsca znajomych (online albo na miejscu). Twój karnet pokrywa tylko
        Twoje miejsce. Każdy może sam się wypisać.
      </Typography>
      <Typography variant="subtitle2">Korzystanie z karnetów znajomego</Typography>
      <Typography variant="body2">
        Przy włączonych wspólnych zapisach możesz poprosić znajomego o zgodę na korzystanie z jego karnetów. Jeśli się zgodzi:
      </Typography>
      <PassUseExplanation perspective="grantee" name="znajomego" />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Rozumiem</Button>
    </DialogActions>
  </Dialog>
);

// Friends + pass-use permissions. "Poproś o korzystanie z karnetów" asks a
// friend to let you charge THEIR seat to THEIR pass when you book them; you
// never see their passes. The friend answers here too and can revoke anytime.
const FriendsTab = ({ user }) => {
  const [friendsData, setFriendsData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Every pass-use step and removing a friend go through a dialog that says
  // what happens, so nothing is agreed to by a stray click.
  const [confirm, setConfirm] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [showBlocked, setShowBlocked] = useState(false);
  const [reporting, setReporting] = useState(null);
  const fetchFriendsPendingCount = useAuthStore((state) => state.fetchFriendsPendingCount);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [friends, perms, notices, blockedList] = await Promise.all([
        api(user, "/friends/"),
        api(user, "/pass-permissions/"),
        api(user, "/friends/notifications"),
        api(user, "/blocks"),
      ]);
      setFriendsData(friends);
      setPermissions(perms);
      setBlocked(blockedList);
      // Keep what was unread highlighted for this visit, but mark it read now.
      setNotifications((prev) => {
        const seenUnread = new Set(prev.filter((n) => !n.read_at).map((n) => n.id));
        return notices.items.map((n) => (seenUnread.has(n.id) ? { ...n, read_at: null } : n));
      });
      setHasMoreNotifications(notices.has_more);
      if (notices.items.some((n) => !n.read_at)) {
        await api(user, "/friends/notifications/read", "POST");
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    }
    fetchFriendsPendingCount();
  }, [user, fetchFriendsPendingCount]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (path, method = "POST", body) => {
    setConfirm(null);
    setBusy(true);
    try {
      await api(user, path, method, body);
      await load();
    } catch (err) {
      // The list may be stale (e.g. the friend withdrew meanwhile): refresh it.
      await load();
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadOlderNotifications = async () => {
    const last = notifications[notifications.length - 1];
    try {
      const page = await api(user, `/friends/notifications?before_id=${last.id}`);
      setNotifications((prev) => [...prev, ...page.items]);
      setHasMoreNotifications(page.has_more);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteNotification = async (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      await api(user, `/friends/notifications/${id}`, "DELETE");
    } catch (err) {
      setError(err.message);
      load();
    }
  };

  const askToBlock = (person) =>
    setConfirm({
      title: `Zablokować ${person.full_name}?`,
      body: <Typography variant="body2">{blockConfirmMessage(person.full_name)}</Typography>,
      confirmLabel: "Zablokuj",
      confirmColor: "error",
      onConfirm: () => run("/blocks", "POST", { user_id: person.id }),
    });

  const askToUnblock = (person) =>
    setConfirm({
      title: `Odblokować ${person.full_name}?`,
      body: (
        <Typography variant="body2">
          Będziecie mogli znów się wyszukać i zaprosić do znajomych. Znajomość nie wróci sama.
        </Typography>
      ),
      confirmLabel: "Odblokuj",
      confirmColor: "primary",
      onConfirm: () => run(`/blocks/${person.id}`, "DELETE"),
    });

  const sendReport = async (reason, details) => {
    const person = reporting;
    await api(user, "/reports", "POST", { user_id: person.id, reason, details });
    setReporting(null);
    setConfirm({
      title: "Dziękujemy za zgłoszenie",
      body: (
        <Typography variant="body2">
          Studio się mu przyjrzy. Czy chcesz też zablokować {person.full_name}? Zniknie z Twoich znajomych i Tablicy.
        </Typography>
      ),
      confirmLabel: "Zablokuj",
      confirmColor: "error",
      onConfirm: () => run("/blocks", "POST", { user_id: person.id }),
      cancelLabel: "Nie teraz",
    });
  };

  const clearNotifications = () =>
    setConfirm({
      title: "Wyczyścić powiadomienia?",
      body: <Typography>Wszystkie przeczytane powiadomienia zostaną usunięte.</Typography>,
      confirmLabel: "Wyczyść",
      confirmColor: "error",
      onConfirm: () => run("/friends/notifications", "DELETE"),
    });

  if (!friendsData && !error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { friends = [], pending_incoming = [], pending_outgoing = [] } = friendsData || {};
  // What I asked of a friend (I am the grantee) and what friends asked of me.
  const askedOf = (friendId) => permissions.find((p) => !p.i_am_owner && p.owner.id === friendId);
  const grantedTo = (friendId) => permissions.find((p) => p.i_am_owner && p.grantee.id === friendId);
  const incomingPassRequests = permissions.filter((p) => p.i_am_owner && p.status === "pending");
  const incomingBookingRequests = friends.filter((f) => f.booking_status === "pending" && !f.booking_requested_by_me);
  const isEmpty = !friends.length && !pending_incoming.length && !pending_outgoing.length;

  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h6">Znajomi</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" startIcon={<HelpOutlineIcon />} onClick={() => setHelpOpen(true)}>
            Jak to działa?
          </Button>
          <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={() => setAddDialogOpen(true)}>
            Dodaj znajomego
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <NotificationsSection
        notifications={notifications}
        hasMore={hasMoreNotifications}
        onLoadMore={loadOlderNotifications}
        onDelete={deleteNotification}
        onClear={clearNotifications}
      />

      {isEmpty && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
          <PeopleIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
          <Typography color="text.secondary" align="center">
            Nie masz jeszcze znajomych
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
            Dodaj znajomego, aby widzieć jego treningi na tablicy w aplikacji i zapisywać się razem na zajęcia
          </Typography>
        </Box>
      )}

      {pending_incoming.length > 0 && (
        <Section title="Zaproszenia do znajomych">
          {pending_incoming.map((item) => (
            <PersonCard key={item.id} person={item.friend} testId="friend-request">
              <Button size="small" color="success" variant="contained" disabled={busy} onClick={() => run(`/friends/${item.id}/accept`)}>
                Akceptuj
              </Button>
              <Button size="small" color="error" disabled={busy} onClick={() => run(`/friends/${item.id}/decline`)}>
                Odrzuć
              </Button>
              <ModerationMenu busy={busy} onReport={() => setReporting(item.friend)} onBlock={() => askToBlock(item.friend)} />
            </PersonCard>
          ))}
        </Section>
      )}

      {incomingBookingRequests.length > 0 && (
        <Section title="Propozycje wspólnych zapisów">
          {incomingBookingRequests.map((f) => (
            <PersonCard key={f.id} person={f.friend} testId="booking-request">
              <Typography variant="caption" color="text.secondary" sx={{ width: "100%" }}>
                Proponuje, żebyście mogli zapisywać się nawzajem na zajęcia.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() =>
                  setConfirm({
                    title: `${f.friend.full_name} proponuje wspólne zapisy`,
                    body: <BookingExplanation name={f.friend.full_name} />,
                    confirmLabel: "Zgadzam się",
                    confirmColor: "success",
                    onConfirm: () => run(`/friends/${f.id}/booking/accept`),
                    secondaryLabel: "Nie zgadzam się",
                    onSecondary: () => run(`/friends/${f.id}/booking/decline`),
                  })
                }
              >
                Sprawdź propozycję
              </Button>
            </PersonCard>
          ))}
        </Section>
      )}

      {incomingPassRequests.length > 0 && (
        <Section title="Prośby o korzystanie z Twoich karnetów">
          {incomingPassRequests.map((p) => (
            <PersonCard key={p.id} person={p.grantee} testId="pass-request">
              <Typography variant="caption" color="text.secondary" sx={{ width: "100%" }}>
                Prosi o zgodę na korzystanie z Twoich karnetów przy wspólnych zapisach.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() =>
                  setConfirm({
                    title: `${p.grantee.full_name} prosi o korzystanie z Twoich karnetów`,
                    body: <PassUseExplanation perspective="owner" name={p.grantee.full_name} />,
                    confirmLabel: "Zgadzam się",
                    confirmColor: "success",
                    onConfirm: () => run(`/pass-permissions/${p.id}/accept`),
                    secondaryLabel: "Nie zgadzam się",
                    onSecondary: () => run(`/pass-permissions/${p.id}/decline`),
                  })
                }
              >
                Sprawdź prośbę
              </Button>
            </PersonCard>
          ))}
        </Section>
      )}

      {pending_outgoing.length > 0 && (
        <Section title="Wysłane zaproszenia">
          {pending_outgoing.map((item) => (
            <PersonCard key={item.id} person={item.friend} testId="friend-outgoing">
              <Chip label="Oczekuje" size="small" variant="outlined" />
            </PersonCard>
          ))}
        </Section>
      )}

      {friends.length > 0 && (
        <>
          {(pending_incoming.length > 0 || pending_outgoing.length > 0) && <Divider sx={{ my: 2 }} />}
          <Section title="Twoi znajomi">
            {friends.map((item) => {
              const asked = askedOf(item.friend.id);
              const granted = grantedTo(item.friend.id);
              return (
                <FriendCard
                  key={item.id}
                  person={item.friend}
                  friendship={item}
                  asked={asked}
                  granted={granted}
                  busy={busy}
                  onProposeBooking={() =>
                    setConfirm({
                      title: `Zaproponować ${item.friend.full_name} wspólne zapisy?`,
                      body: <BookingExplanation name={item.friend.full_name} />,
                      confirmLabel: "Zaproponuj",
                      confirmColor: "primary",
                      onConfirm: () => run(`/friends/${item.id}/booking/request`),
                    })
                  }
                  onWithdrawBooking={() => run(`/friends/${item.id}/booking`, "DELETE")}
                  onDisableBooking={() =>
                    setConfirm({
                      title: "Wyłączyć wspólne zapisy?",
                      body: (
                        <Typography variant="body2">
                          Nie będziecie mogli zapisywać się nawzajem na zajęcia, a zgody na karnety między Wami zostaną
                          usunięte. Zapisy zrobione wcześniej się nie zmienią. Nadal będziecie znajomymi.
                        </Typography>
                      ),
                      confirmLabel: "Wyłącz",
                      confirmColor: "warning",
                      onConfirm: () => run(`/friends/${item.id}/booking`, "DELETE"),
                    })
                  }
                  onAskForPasses={() =>
                    setConfirm({
                      title: `Poprosić ${item.friend.full_name} o korzystanie z karnetów?`,
                      body: <PassUseExplanation perspective="grantee" name={item.friend.full_name} />,
                      confirmLabel: "Wyślij prośbę",
                      confirmColor: "primary",
                      onConfirm: () => run("/pass-permissions/request", "POST", { friend_id: item.friend.id }),
                    })
                  }
                  onRevokePasses={() =>
                    setConfirm({
                      title: "Cofnąć zgodę na karnety?",
                      body: (
                        <Typography variant="body2">
                          {item.friend.full_name} nie będzie mógł/mogła już płacić Twoim karnetem za Twoje miejsce.
                          Zapisy zrobione wcześniej się nie zmienią.
                        </Typography>
                      ),
                      confirmLabel: "Cofnij zgodę",
                      confirmColor: "warning",
                      onConfirm: () => run(`/pass-permissions/${granted.id}`, "DELETE"),
                    })
                  }
                  onRemove={() =>
                    setConfirm({
                      title: `Usunąć ${item.friend.full_name} ze znajomych?`,
                      body: (
                        <Typography variant="body2">
                          Przestaniecie widzieć swoje treningi i nie będziecie mogli zapisywać się razem, a zgody na
                          karnety między Wami zostaną usunięte.
                        </Typography>
                      ),
                      confirmLabel: "Usuń znajomego",
                      confirmColor: "error",
                      onConfirm: () => run(`/friends/${item.id}`, "DELETE"),
                    })
                  }
                  onReport={() => setReporting(item.friend)}
                  onBlock={() => askToBlock(item.friend)}
                />
              );
            })}
          </Section>
        </>
      )}

      {blocked.length > 0 && (
        <Box sx={{ mt: 3 }} data-testid="blocked-section">
          <Button size="small" onClick={() => setShowBlocked((v) => !v)} sx={{ textTransform: "none" }}>
            {showBlocked ? "Ukryj zablokowanych" : `Zablokowani (${blocked.length})`}
          </Button>
          {showBlocked && (
            <Section title="Zablokowani">
              {blocked.map((b) => (
                <PersonCard key={b.user.id} person={b.user} testId="blocked">
                  <Button size="small" disabled={busy} onClick={() => askToUnblock(b.user)}>
                    Odblokuj
                  </Button>
                </PersonCard>
              ))}
            </Section>
          )}
        </Box>
      )}

      <ReportDialog person={reporting} onClose={() => setReporting(null)} onSubmit={sendReport} />
      <AddFriendDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} user={user} onSuccess={load} />
      <HowItWorksDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} fullWidth maxWidth="sm" data-testid="friends-confirm">
        {confirm && (
          <>
            <DialogTitle>{confirm.title}</DialogTitle>
            <DialogContent>{confirm.body}</DialogContent>
            <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
              <Button onClick={() => setConfirm(null)} disabled={busy}>
                {confirm.cancelLabel || "Anuluj"}
              </Button>
              {confirm.secondaryLabel && (
                <Button color="error" onClick={confirm.onSecondary} disabled={busy}>
                  {confirm.secondaryLabel}
                </Button>
              )}
              <Button variant="contained" color={confirm.confirmColor} onClick={confirm.onConfirm} disabled={busy}>
                {confirm.confirmLabel}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default FriendsTab;
